/**
 * PATH: packages/tenant-shared/src/settings/emailSignature.ts
 * FILE_ID: TENANTSHARED-EMAILSIG-STORE-001
 * PURPOSE: The ONE canonical store for the operator's email signature —
 *          the HTML block appended to outgoing messages. Wraps the
 *          two-tier preference pattern (instant localStorage cache IN
 *          FRONT of the durable per-user/per-org view-preference store)
 *          so every surface that edits or applies the signature reads
 *          and writes the SAME value.
 * WHY:     The signature used to live in two disconnected places — a
 *          plain-text field in Settings (`/api/settings/email/`,
 *          `email_preferences.signature`) that NOTHING read at send
 *          time, and the rich HTML the composer actually applied
 *          (localStorage `email.signature.html.v1` + view-pref
 *          `email:signature`). So a signature "set up" in Settings
 *          never appeared on sent mail. This module collapses both onto
 *          the HTML store the send path already uses, so Settings and
 *          the composer drive one signature.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────┐   ┌──────────────────────┐
 *   │ edit (any UI)│ → │ saveSignature │ → │ localStorage + server│
 *   └──────────────┘   └───────────────┘   └──────────────────────┘
 *   ┌──────────────┐   ┌───────────────┐   ┌──────────────────────┐
 *   │ compose seed │ ← │ load / fetch  │ ← │ localStorage + server│
 *   └──────────────┘   └───────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - preferences/dbViewPreference: durable server I/O (fail-soft)
 *   - utils/tenantStorage: tlsKey() for the tenant-scoped cache key
 * RELATED:
 *   - settings/SignatureField.tsx: the shared editor surface
 *   - packages/email-hub/src/feature/pages/_composeHelpers.ts: thin
 *     re-export shim so the composer keeps its existing import names
 */
import {
  fetchServerState,
  scheduleServerSave,
} from '../preferences/dbViewPreference'
import { tlsKey } from '../utils/tenantStorage'
import {
  BEAVER_MASCOT_DATA_URI,
  BEAVER_MASCOT_HEIGHT,
  BEAVER_MASCOT_WIDTH,
} from './beaverMascot'

/** Durable view-preference scope. MUST stay 'email:signature' — existing
 *  operators already have signatures stored under this key. */
export const EMAIL_SIGNATURE_SCOPE = 'email:signature'

/** Instant localStorage cache key (tenant-scoped). MUST stay
 *  'email.signature.html.v1' — the composer's synchronous reads and any
 *  cached signatures depend on it. */
export const EMAIL_SIGNATURE_CACHE_KEY = tlsKey('email.signature.html.v1')

interface SignatureBlob {
  html: string
}

// ---------------------------------------------------------------------------
// Fail-closed cross-login guard (BUG: cross-org signature contamination).
//
// `tlsKey` namespaces the localStorage cache by BUILD-time tenant slug only —
// every user/organisation that logs into the same SPA on the same browser
// shares the key. A signature cached by user A (org A) was seeded verbatim
// into user B's compose (org B) before the durable per-user fetch resolved
// (operator report 2026-07-17: Mark Newman / Newmark Constructions signature
// in an abhishek@vastdreams.com compose). The cache is therefore UNTRUSTED
// until, in THIS page load, either (a) the durable per-user store has been
// fetched (which overwrites the cache with the logged-in user's truth), or
// (b) the operator saved a signature themselves. Until then every read
// returns empty — a missing signature is a nuisance; another organisation's
// signature on outgoing mail is a tenant-boundary breach. Fail closed.
// ---------------------------------------------------------------------------

let _defaultSignatureTrusted = false
let _byAccountTrusted = false

/** True once the default-signature cache reflects the logged-in user. */
export function signatureCacheIsTrusted(): boolean {
  return _defaultSignatureTrusted
}

/** Test-only: reset the per-page-load trust flags. */
export function __resetSignatureTrustForTests(): void {
  _defaultSignatureTrusted = false
  _byAccountTrusted = false
}

/** Test-only: mark both caches trusted, as if durable hydration ran. */
export function __trustSignatureCacheForTests(): void {
  _defaultSignatureTrusted = true
  _byAccountTrusted = true
}

// ---------------------------------------------------------------------------
// Per-account (per-mailbox) signature overrides.
//
// The single signature above stays the DEFAULT — applied to any mailbox
// without its own override, so every existing operator is unaffected.
// This second store maps a lowercased mailbox address → its signature
// HTML. ``resolveSignatureForAccount`` is the one read the send/compose
// path uses: override-if-set, else the default. Both tiers (instant
// localStorage cache + durable per-user/per-org store) mirror the
// default signature's pattern so per-account signatures also follow the
// operator across devices.
// ---------------------------------------------------------------------------

/** Durable scope for the per-account override map. New scope (no
 *  existing data) so introducing it can't disturb the default. */
export const EMAIL_SIGNATURE_BY_ACCOUNT_SCOPE = 'email:signatures.byAccount'

/** Instant cache key for the per-account override map (tenant-scoped). */
export const EMAIL_SIGNATURE_BY_ACCOUNT_CACHE_KEY = tlsKey(
  'email.signature.byAccount.v1',
)

export type SignatureByAccount = Record<string, string>


/** Stored marker meaning "this operator/mailbox has DELIBERATELY chosen
 *  no signature" — distinct from '' (nothing stored yet). Clearing the
 *  editor stores '' which still falls back to the branded default; saving
 *  this sentinel suppresses every signature (default included) so a true
 *  "No signature" send is possible. Kept intentionally un-HTML-like so it
 *  can never collide with real signature markup. */
export const NO_SIGNATURE_SENTINEL = '__quotech_no_signature__'

/** True when a stored value is the explicit no-signature sentinel.
 *  Pure: value -> boolean. */
export function isNoSignatureSentinel(
  value: string | null | undefined,
): boolean {
  return (value || '').trim() === NO_SIGNATURE_SENTINEL
}

type StoredSignature =
  | { kind: 'optout' }
  | { kind: 'unset' }
  | { kind: 'html'; html: string }

/** Classify a stored signature value into the three states the resolver
 *  cares about: an explicit opt-out, nothing meaningful stored, or real
 *  HTML. Pure: value -> state. */
function interpretStoredSignature(
  value: string | null | undefined,
): StoredSignature {
  if (isNoSignatureSentinel(value)) return { kind: 'optout' }
  if (signatureIsBlank(value)) return { kind: 'unset' }
  return { kind: 'html', html: value as string }
}

/** Coerce an editor/UI value to what should be persisted: the sentinel
 *  verbatim, '' for blank, or the html as-is. Pure: html -> stored. */
function toStoredSignatureValue(html: string | null | undefined): string {
  if (isNoSignatureSentinel(html)) return NO_SIGNATURE_SENTINEL
  return signatureIsBlank(html) ? '' : (html as string)
}

function normaliseAddress(email: string | null | undefined): string {
  return (email || '').trim().toLowerCase()
}

/** Synchronous read of the cached per-account override map. Always
 *  returns an object ('{}' when none / storage unavailable). Returns
 *  '{}' until the cache is trusted for the logged-in user (see the
 *  fail-closed guard above) — the raw bytes may belong to whoever was
 *  logged in on this browser before. */
export function loadCachedSignaturesByAccount(): SignatureByAccount {
  if (typeof window === 'undefined') return {}
  if (!_byAccountTrusted) return {}
  try {
    const raw = window.localStorage.getItem(EMAIL_SIGNATURE_BY_ACCOUNT_CACHE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as SignatureByAccount)
      : {}
  } catch {
    return {}
  }
}

/** Persist a per-account override to BOTH tiers. A blank html removes
 *  the override (the mailbox falls back to the default signature).
 *  A blank address is a no-op (use ``saveSignatureHtml`` for the
 *  default). Fail-soft. */
export function saveSignatureForAccount(
  email: string | null | undefined, html: string,
): void {
  const key = normaliseAddress(email)
  if (!key) return
  const map = loadCachedSignaturesByAccount()
  // Sentinel ("No signature") is a real, persisted choice; only a truly
  // blank value removes the override so the mailbox falls back to default.
  const value = toStoredSignatureValue(html)
  if (value) map[key] = value
  else delete map[key]
  try {
    window.localStorage.setItem(
      EMAIL_SIGNATURE_BY_ACCOUNT_CACHE_KEY, JSON.stringify(map),
    )
  } catch {
    /* private mode / quota — server tier below still persists */
  }
  // The operator just authored this map in this session — trust it.
  _byAccountTrusted = true
  scheduleServerSave<SignatureByAccount>(EMAIL_SIGNATURE_BY_ACCOUNT_SCOPE, map)
}

/** One-shot pull of the durable per-account map; writes it into the
 *  cache so synchronous reads pick it up. Returns the map (possibly
 *  empty). Fail-soft. */
export async function fetchDurableSignaturesByAccount(): Promise<SignatureByAccount> {
  const map = await fetchServerState<SignatureByAccount>(
    EMAIL_SIGNATURE_BY_ACCOUNT_SCOPE, {},
  )
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    try {
      window.localStorage.setItem(
        EMAIL_SIGNATURE_BY_ACCOUNT_CACHE_KEY, JSON.stringify(map),
      )
    } catch {
      /* ignore cache write failures */
    }
    // Cache now mirrors the logged-in user's server truth.
    _byAccountTrusted = true
    return map as SignatureByAccount
  }
  // null = user has no overrides OR the fetch failed. Either way the
  // cache stays UNTRUSTED (it may be a previous login's data) and reads
  // keep returning '{}' — never someone else's overrides.
  return {}
}

/** The signature to apply for a given From mailbox: its own override if
 *  set and non-blank, otherwise the single default signature. Pure read
 *  of the synchronous caches — safe on the compose seed path. */
export function resolveSignatureForAccount(
  email: string | null | undefined,
): string {
  const key = normaliseAddress(email)
  if (key) {
    const acct = interpretStoredSignature(loadCachedSignaturesByAccount()[key])
    if (acct.kind === 'optout') return ''
    if (acct.kind === 'html') return acct.html
    // 'unset' → fall through to the default (all-mailboxes) signature.
  }
  const def = interpretStoredSignature(loadCachedSignatureHtml())
  if (def.kind === 'html') return def.html
  // 'optout' or 'unset' at the default level → no per-account signature.
  return ''
}

/** The signature stored for a specific scope, for the editor to load:
 *  the default when ``email`` is blank, else that mailbox's override
 *  (which may be '' when it has none yet). The opt-out sentinel renders
 *  as a blank editor — the modal reads the opt-out state separately via
 *  ``scopeIsOptedOut`` so the "No signature" toggle reflects it. */
export function loadSignatureForScope(
  email: string | null | undefined,
): string {
  const raw = loadRawSignatureForScope(email)
  return isNoSignatureSentinel(raw) ? '' : raw
}

/** Raw stored value for a scope (may be the sentinel). Internal-ish but
 *  exported so the editor can detect a saved opt-out. */
export function loadRawSignatureForScope(
  email: string | null | undefined,
): string {
  const key = normaliseAddress(email)
  if (!key) return loadCachedSignatureHtml()
  return loadCachedSignaturesByAccount()[key] || ''
}

/** True when the given scope has an explicit "No signature" choice saved
 *  (the sentinel), as opposed to merely empty. Pure read of the cache. */
export function scopeIsOptedOut(email: string | null | undefined): boolean {
  return isNoSignatureSentinel(loadRawSignatureForScope(email))
}

/** True when the HTML carries no real text (only tags / &nbsp; / space).
 *  Pure: html -> boolean. */
export function signatureIsBlank(html: string | null | undefined): boolean {
  return (html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .length === 0
}

/** Synchronous read of the cached signature HTML. Always returns a
 *  string ('' when none / storage unavailable). Safe off-window.
 *  Returns '' until the cache is trusted for the logged-in user (see
 *  the fail-closed guard above). */
export function loadCachedSignatureHtml(): string {
  if (typeof window === 'undefined') return ''
  if (!_defaultSignatureTrusted) return ''
  try {
    return window.localStorage.getItem(EMAIL_SIGNATURE_CACHE_KEY) || ''
  } catch {
    return ''
  }
}

/** Persist the signature to BOTH tiers. An empty/blank html clears it.
 *  Fail-soft: the localStorage write degrades silently in private mode,
 *  and the server write is debounced + swallows offline/not-deployed. */
export function saveSignatureHtml(html: string): void {
  // Sentinel ("No signature") persists verbatim so the resolver can
  // suppress the branded default; only a truly blank value clears.
  const value = toStoredSignatureValue(html)
  try {
    if (value) window.localStorage.setItem(EMAIL_SIGNATURE_CACHE_KEY, value)
    else window.localStorage.removeItem(EMAIL_SIGNATURE_CACHE_KEY)
  } catch {
    /* private mode / quota — server tier below still persists */
  }
  // The operator just authored this signature in this session — trust it.
  _defaultSignatureTrusted = true
  scheduleServerSave<SignatureBlob>(EMAIL_SIGNATURE_SCOPE, { html: value })
}

/** One-shot pull of the durable signature. Writes the value into the
 *  localStorage cache so the synchronous reads pick it up, then returns
 *  it. Returns null when the operator has none on the server / is
 *  offline (callers keep whatever they cached). Fail-soft. */
export async function fetchDurableSignatureHtml(): Promise<string | null> {
  const blob = await fetchServerState<SignatureBlob>(EMAIL_SIGNATURE_SCOPE, { html: '' })
  if (blob && typeof blob.html === 'string') {
    try {
      if (blob.html) window.localStorage.setItem(EMAIL_SIGNATURE_CACHE_KEY, blob.html)
      else window.localStorage.removeItem(EMAIL_SIGNATURE_CACHE_KEY)
    } catch {
      /* ignore cache write failures */
    }
    // Cache now mirrors the logged-in user's server truth.
    _defaultSignatureTrusted = true
    return blob.html
  }
  // null = user has no saved signature OR the fetch failed. The cache
  // stays UNTRUSTED and reads keep returning '' — for a user with no
  // signature that IS the truth; for an offline user losing their own
  // signature for a page load beats sending another organisation's.
  return null
}

const _ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
}

/** Render the stored signature HTML down to readable plain text for a
 *  basic <textarea> editor: block/line tags become newlines, remaining
 *  tags are stripped, common entities decoded. Pure: html -> text. */
export function signatureHtmlToPlainText(html: string | null | undefined): string {
  if (!html) return ''
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&#(\d+);/g, (_m, code) => {
      const n = Number(code)
      return Number.isFinite(n) ? String.fromCharCode(n) : _m
    })
    .replace(/&[a-z#0-9]+;/gi, (m) => _ENTITIES[m.toLowerCase()] ?? m)
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim()
}

/** Wrap operator-typed plain text as safe signature HTML: escape the
 *  five HTML-significant characters, newlines become <br>. Pure:
 *  text -> html. '' in → '' out (a cleared signature). */
export function plainTextToSignatureHtml(text: string | null | undefined): string {
  const trimmed = (text || '').replace(/\r\n/g, '\n').trim()
  if (!trimmed) return ''
  const escaped = trimmed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
  return escaped.replace(/\n/g, '<br>')
}

// ---------------------------------------------------------------------------
// Default (brand) signature.
//
// When an operator has saved no signature of their own, every outgoing
// message still carries a Quotech-branded block with the foreman beaver,
// the operator's name + From address, and a short tagline — "the beaver
// on every email". The mascot is embedded as a base64 data: URI so the
// backend inline-image step (email_inline_images.py) turns it into a real
// cid: attachment on send, the only form Gmail/Outlook reliably render.
// The moment an operator saves their own signature it REPLACES this.
// ---------------------------------------------------------------------------

/** Marks an auto-generated default signature so a surface can tell it
 *  apart from one the operator saved. Not load-bearing (the compose seed
 *  tracks what it inserted) — useful for debugging + a future opt-out. */
export const DEFAULT_SIGNATURE_MARKER = 'data-quotech-default-sig'

/** Short brand tagline for the default signature: ONE line, no em dash
 *  (operator feedback 2026-06-23: "keep it short and simple, no slop").
 *  Operators who want different wording just save their own signature. */
export const DEFAULT_SIGNATURE_TAGLINE = 'AI-powered ERP for construction.'

const _SIG_BRAND_TEAL = '#0f9488'
const _SIG_INK = '#0f172a'
const _SIG_MUTED = '#475569'

/** Escape the five HTML-significant characters in untrusted identity
 *  fields (mailbox display name / address) before interpolating them
 *  into the signature markup. Pure: text -> safe text. */
function escapeSignatureText(value: string | null | undefined): string {
  return (value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export interface SignatureIdentity {
  /** Operator display name for the From mailbox (e.g. "Mark Newman").
   *  A blank name simply omits the name line. */
  name?: string | null
  /** From mailbox address, shown as a mailto link. */
  email?: string | null
}

/** Build the Quotech-branded DEFAULT signature: the foreman beaver
 *  (inline base64, so it travels as a cid attachment), the operator's
 *  name + email, the brand line and a short tagline. Pure I→O:
 *  identity -> HTML. Table-based + fully inline-styled so it survives the
 *  outbound email-HTML pass and renders consistently in Gmail/Outlook. */
export function buildDefaultSignatureHtml(
  identity?: SignatureIdentity,
): string {
  const name = escapeSignatureText((identity?.name || '').trim())
  const email = escapeSignatureText((identity?.email || '').trim())
  const dispW = Math.round(BEAVER_MASCOT_WIDTH / 2)
  const dispH = Math.round(BEAVER_MASCOT_HEIGHT / 2)
  const nameLine = name
    ? `<div style="font-weight:700;color:${_SIG_INK};font-size:14px;">${name}</div>`
    : ''
  const emailLink = email
    ? `<a href="mailto:${email}" style="color:${_SIG_BRAND_TEAL};text-decoration:none;">${email}</a>`
    : ''
  const sep = email ? ' &nbsp;&middot;&nbsp; ' : ''
  return (
    `<table ${DEFAULT_SIGNATURE_MARKER}="1" role="presentation" cellpadding="0" `
    + `cellspacing="0" style="border-collapse:collapse;`
    + `font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;"><tbody><tr>`
    + `<td style="vertical-align:top;padding:0 12px 0 0;">`
    + `<img src="${BEAVER_MASCOT_DATA_URI}" width="${dispW}" height="${dispH}" `
    + `alt="Quotech" style="display:block;width:${dispW}px;height:${dispH}px;" />`
    + `</td><td style="vertical-align:top;font-size:13px;line-height:1.5;`
    + `color:${_SIG_MUTED};">`
    + nameLine
    + `<div style="font-weight:700;color:${_SIG_BRAND_TEAL};font-size:14px;">`
    + `Quotech.AI</div>`
    + `<div style="color:${_SIG_MUTED};">${DEFAULT_SIGNATURE_TAGLINE}</div>`
    + `<div style="margin-top:2px;">${emailLink}${sep}`
    + `<a href="https://quotech.ai" style="color:${_SIG_BRAND_TEAL};`
    + `text-decoration:none;">quotech.ai</a></div>`
    + `</td></tr></tbody></table>`
  )
}

/** The signature to seed into a compose for a given From mailbox: the
 *  operator's own (per-account override or saved default) when set, else
 *  EMPTY. We deliberately do NOT seed the Quotech-branded beaver default
 *  here any more: the backend appends the single "Powered by Quotech" GTM
 *  footer (organisation/services/email_signature.py) at SEND time, which
 *  is non-editable and idempotent. Seeding the beaver card into the
 *  editable body as well meant a default-signature operator got TWO
 *  Quotech blocks on the received mail (operator 2026-06-29). Returning ''
 *  leaves the body clean; the GTM footer is the one brand block.
 *  ``buildDefaultSignatureHtml`` + ``identity`` are kept (still exported /
 *  unit-tested) so the branded block can be reinstated without a rebuild.
 *  Pure read of the synchronous caches + identity. */
export function resolveSignatureWithDefault(
  email: string | null | undefined,
  _identity?: SignatureIdentity,
): string {
  // Opt-out (sentinel) or a real saved signature both come through
  // resolveSignatureForAccount. Unset → empty (backend GTM footer is the
  // single brand block — do NOT seed the beaver card into the body).
  const own = resolveSignatureForAccount(email)
  if (own && !signatureIsBlank(own) && !isNoSignatureSentinel(own)) return own
  return ''
}
